<script setup lang="ts">
import { __ } from '@@/bits/intl'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useGifWithReducedMotion } from '@@/vuecomposables/useGifWithReducedMotion'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    imageSrc: string
    width: number
    height: number
    originalImageWidth: number
    originalImageHeight: number
    aspectRatio?: number
    roundedClass?: string
    imageLuminance?: string
    xPlayPauseButton?: boolean
    position?: 'center' | 'bottom-end'
  }>(),
  {
    aspectRatio: undefined,
    roundedClass: 'rounded-sm',
    imageLuminance: undefined,
    xPlayPauseButton: true,
    position: 'bottom-end',
  },
)

const emit = defineEmits(['load'])

const { currentUser } = storeToRefs(useSurfaceCurrentUserStore())
const { isGifPaused, firstFrameDataUrl, toggleGifPause } = useGifWithReducedMotion({
  imageUrl: computed(() => props.imageSrc),
  isGif: ref(true),
  currentUser,
})
</script>

<template>
  <div class="relative pointer-events-none">
    <ImageThumbnail
      v-if="isGifPaused && firstFrameDataUrl"
      :class="roundedClass"
      :src="firstFrameDataUrl"
      :width="width"
      :height="height"
      :original-image-width="originalImageWidth"
      :original-image-height="originalImageHeight"
      :aspect-ratio="aspectRatio"
      :process-image="true"
      :lazy-loading="false"
      draggable="false"
      @load="emit('load')"
    />
    <ImageThumbnail
      v-else
      :class="roundedClass"
      :src="imageSrc"
      :width="width"
      :height="height"
      :original-image-width="originalImageWidth"
      :original-image-height="originalImageHeight"
      :aspect-ratio="aspectRatio"
      :process-image="true"
      :lazy-loading="false"
      draggable="false"
      @load="emit('load')"
    />
    <OzIconButton
      v-if="xPlayPauseButton"
      :class="[
        'absolute z-10',
        position === 'center' ? 'top-1/2 start-1/2 -translate-s-1/2 -translate-y-1/2' : 'bottom-1 end-1',
        isGifPaused ? 'opacity-100' : 'opacity-0',
        'focus:opacity-100',
        'transition-opacity',
        'group-hover:opacity-100',
        '!ring-grape-500 dark:!ring-canary-500',
        'pointer-events-auto',
      ]"
      :dark-mode="imageLuminance === 'dark'"
      :label="isGifPaused ? __('Play GIF') : __('Pause GIF')"
      :color-scheme="OzIconButtonColorScheme.SecondaryClear"
      :size-preset="OzIconButtonSizePreset.H24px"
      :icon-name="isGifPaused ? 'play' : 'pause'"
      @click.stop.prevent="toggleGifPause"
    />
  </div>
</template>
